import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, InputNumber, Select, Switch, Tag, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import SelectLn2Message from "../../../../components/select/SelectLn2Message";
import FormInputText from "../../../../components/form/FormInputText";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import DataValueEditor from "./DataValueEditor";
import AttributeSelect from "./AttributeSelect";
// FLATBUFFER ENUMS
import { AttributeId } from "../../../../../flatbuffers/generated/opc-ua/attribute-id";
import { FilterOperator } from "../../../../../flatbuffers/generated/opc-ua/filter-operator";
// INTERFACES
import type {
  OpcUaContentFilterElementForm,
  OpcUaEventMonitorForm,
  OpcUaEventToMessageRow,
  OpcUaFilterOperandForm,
  OpcUaNodeIdForm,
  OpcUaSimpleAttributeOperandForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;
const { Option } = Select;

const BLANK_ATTRIBUTE_OPERAND: OpcUaSimpleAttributeOperandForm = {
  typeDefinitionNode: null,
  browsePath: [],
  attributeId: AttributeId.Value,
  indexRange: "",
};

const BLANK_FILTER_ELEMENT: OpcUaContentFilterElementForm = {
  operator: FilterOperator.Equals,
  operands: [],
};

const FILTER_OPERATOR_OPTIONS: { value: FilterOperator; label: string }[] = [
  { value: FilterOperator.Equals, label: "Equals" },
  { value: FilterOperator.IsNull, label: "IsNull" },
  { value: FilterOperator.GreaterThan, label: "GreaterThan" },
  { value: FilterOperator.LessThan, label: "LessThan" },
  { value: FilterOperator.GreaterThanOrEqual, label: "GreaterThanOrEqual" },
  { value: FilterOperator.LessThanOrEqual, label: "LessThanOrEqual" },
  { value: FilterOperator.Like, label: "Like" },
  { value: FilterOperator.Not, label: "Not" },
  { value: FilterOperator.Between, label: "Between" },
  { value: FilterOperator.InList, label: "InList" },
  { value: FilterOperator.And, label: "And" },
  { value: FilterOperator.Or, label: "Or" },
  { value: FilterOperator.Cast, label: "Cast" },
  { value: FilterOperator.InView, label: "InView" },
  { value: FilterOperator.OfType, label: "OfType" },
  { value: FilterOperator.RelatedTo, label: "RelatedTo" },
  { value: FilterOperator.BitwiseAnd, label: "BitwiseAnd" },
  { value: FilterOperator.BitwiseOr, label: "BitwiseOr" },
];

function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

function describeNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}

function describeOperand(operand: OpcUaFilterOperandForm): string {
  if (operand.kind === "literal") return `literal(${operand.value.kind}=${operand.value.value})`;
  if (operand.kind === "attribute") return `attr(${operand.operand.browsePath.join("/")})`;
  return `element(#${operand.index})`;
}

/** One SELECT clause: which attribute of which event-type hierarchy to report, addressed by an ordered browse-name path. */
function SimpleAttributeOperandFields({
  operand,
  onChangeOperand,
}: {
  operand: OpcUaSimpleAttributeOperandForm;
  onChangeOperand: (operand: OpcUaSimpleAttributeOperandForm) => void;
}) {
  const onChangeBrowsePathCsv = (csv: string) => {
    const names = csv
      .split("/")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    onChangeOperand({ ...operand, browsePath: names });
  };

  return (
    <div className="row">
      <div className="col-12 col-md-6 mt-2">
        <FormInputText
          inputValue={operand.browsePath.join("/")}
          inputLabelName="Browse Path (slash-separated, e.g. EventType/Severity)"
          inputOnChange={onChangeBrowsePathCsv}
          inputVariableName="browsePath"
          isRequired={false}
          disabledInput={false}
        />
      </div>
      <div className="col-12 col-md-4 mt-2">
        <AttributeSelect
          value={operand.attributeId}
          onChange={(attributeId) => onChangeOperand({ ...operand, attributeId })}
        />
      </div>
      <div className="col-12 col-md-2 mt-2">
        <FormInputText
          inputValue={operand.indexRange}
          inputLabelName="Index Range"
          inputOnChange={(value) => onChangeOperand({ ...operand, indexRange: value })}
          inputVariableName="indexRange"
          isRequired={false}
          disabledInput={false}
        />
      </div>
    </div>
  );
}

/**
 * A single WHERE-clause element's operand editor. `ElementOperand` (a
 * self-reference by index into the filter's element list) is decode-only
 * per the schema round-trip — shown as a read-only summary but not
 * authorable here.
 */
function FilterOperandEditor({
  operand,
  onChangeOperand,
}: {
  operand: OpcUaFilterOperandForm;
  onChangeOperand: (operand: OpcUaFilterOperandForm) => void;
}) {
  if (operand.kind === "element") {
    return (
      <div className="mt-2">
        <Tag color="default">Element reference #{operand.index} (read-only)</Tag>
      </div>
    );
  }

  return (
    <div
      className="mt-2"
      style={{ backgroundColor: "#161616", borderRadius: "5px", padding: "8px" }}
    >
      <Select
        className="generalStyles__width100"
        size="large"
        value={operand.kind}
        onChange={(kind: "literal" | "attribute") =>
          onChangeOperand(
            kind === "literal"
              ? { kind: "literal", value: { kind: "string", value: "" } }
              : { kind: "attribute", operand: { ...BLANK_ATTRIBUTE_OPERAND } },
          )
        }
      >
        <Option value="literal">Literal Value</Option>
        <Option value="attribute">Simple Attribute</Option>
      </Select>
      {operand.kind === "literal" && (
        <DataValueEditor
          value={operand.value}
          onChange={(value) => onChangeOperand({ kind: "literal", value })}
        />
      )}
      {operand.kind === "attribute" && (
        <SimpleAttributeOperandFields
          operand={operand.operand}
          onChangeOperand={(updated) => onChangeOperand({ kind: "attribute", operand: updated })}
        />
      )}
    </div>
  );
}

/** One flat WHERE-clause element: an operator applied to an ordered list of operands. */
function ContentFilterElementEditor({
  element,
  onChangeElement,
}: {
  element: OpcUaContentFilterElementForm;
  onChangeElement: (element: OpcUaContentFilterElementForm) => void;
}) {
  const onAddOperand = () => {
    onChangeElement({
      ...element,
      operands: [...element.operands, { kind: "literal", value: { kind: "string", value: "" } }],
    });
  };
  const onRemoveOperand = (index: number) => {
    onChangeElement({ ...element, operands: element.operands.filter((_, i) => i !== index) });
  };
  const onChangeOperandAt = (index: number, operand: OpcUaFilterOperandForm) => {
    const operands = [...element.operands];
    operands[index] = operand;
    onChangeElement({ ...element, operands });
  };

  return (
    <div
      style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
      className="mb-2"
    >
      <span className="generalStyles__inputLabel">Operator</span>
      <Select
        className="generalStyles__width100"
        size="large"
        value={element.operator}
        onChange={(operator: FilterOperator) => onChangeElement({ ...element, operator })}
        style={{ marginBottom: 8 }}
      >
        {FILTER_OPERATOR_OPTIONS.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      <span className="generalStyles__inputLabel">Operands ({element.operands.length})</span>
      {element.operands.map((operand, index) => (
        <div key={`${index}-${describeOperand(operand)}`} className="generalStyles__flex">
          <div style={{ width: "100%" }}>
            <FilterOperandEditor
              operand={operand}
              onChangeOperand={(o) => onChangeOperandAt(index, o)}
            />
          </div>
          <Tooltip title="Remove operand">
            <Button
              onClick={() => onRemoveOperand(index)}
              className="buttonStyle__29"
              shape="circle"
              icon={<FontAwesomeIcon icon={faTrash} />}
            />
          </Tooltip>
        </div>
      ))}
      <Button onClick={onAddOperand} className="buttonStyle__28 mt-2">
        <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
        Add Operand
      </Button>
    </div>
  );
}

/** Event/alarm subscription: which node, SELECT clauses, a flat WHERE-clause element list, subscription name, and queue size. */
function EventMonitorForm({
  monitor,
  onChangeMonitor,
}: {
  monitor: OpcUaEventMonitorForm;
  onChangeMonitor: (monitor: OpcUaEventMonitorForm) => void;
}) {
  const onAddSelectClause = () => {
    onChangeMonitor({
      ...monitor,
      selectClauses: [...monitor.selectClauses, { ...BLANK_ATTRIBUTE_OPERAND }],
    });
  };
  const onRemoveSelectClause = (index: number) => {
    onChangeMonitor({
      ...monitor,
      selectClauses: monitor.selectClauses.filter((_, i) => i !== index),
    });
  };
  const onChangeSelectClauseAt = (index: number, operand: OpcUaSimpleAttributeOperandForm) => {
    const selectClauses = [...monitor.selectClauses];
    selectClauses[index] = operand;
    onChangeMonitor({ ...monitor, selectClauses });
  };

  const onAddFilterElement = () => {
    onChangeMonitor({
      ...monitor,
      whereClause: [...monitor.whereClause, { ...BLANK_FILTER_ELEMENT }],
    });
  };
  const onRemoveFilterElement = (index: number) => {
    onChangeMonitor({ ...monitor, whereClause: monitor.whereClause.filter((_, i) => i !== index) });
  };
  const onChangeFilterElementAt = (index: number, element: OpcUaContentFilterElementForm) => {
    const whereClause = [...monitor.whereClause];
    whereClause[index] = element;
    onChangeMonitor({ ...monitor, whereClause });
  };

  return (
    <div>
      <NodeIdEditor
        node={monitor.node}
        onChangeNode={(node) => onChangeMonitor({ ...monitor, node })}
      />
      <div className="row mt-2">
        <div className="col-12 col-md-6">
          <FormInputText
            inputValue={monitor.subscriptionName}
            inputLabelName="Subscription Name"
            inputOnChange={(value) => onChangeMonitor({ ...monitor, subscriptionName: value })}
            inputVariableName="subscriptionName"
            isRequired={false}
            disabledInput={false}
          />
        </div>
        <div className="col-12 col-md-6">
          <span className="generalStyles__inputLabel">Queue Size</span>
          <InputNumber
            className="generalStyles__width100"
            size="large"
            min={0}
            value={monitor.queueSize}
            onChange={(v) => onChangeMonitor({ ...monitor, queueSize: v ?? 0 })}
          />
        </div>
      </div>
      <div className="mt-3">
        <span className="generalStyles__inputLabel">
          Select Clauses ({monitor.selectClauses.length})
        </span>
        {monitor.selectClauses.map((clause, index) => (
          <div key={`${index}-${clause.browsePath.join("/")}`} className="generalStyles__flex mb-2">
            <div style={{ width: "100%" }}>
              <SimpleAttributeOperandFields
                operand={clause}
                onChangeOperand={(updated) => onChangeSelectClauseAt(index, updated)}
              />
            </div>
            <Tooltip title="Remove select clause">
              <Button
                onClick={() => onRemoveSelectClause(index)}
                className="buttonStyle__29"
                shape="circle"
                icon={<FontAwesomeIcon icon={faTrash} />}
              />
            </Tooltip>
          </div>
        ))}
        <Button onClick={onAddSelectClause} className="buttonStyle__28">
          <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
          Add Select Clause
        </Button>
      </div>
      <div className="mt-3">
        <span className="generalStyles__inputLabel">
          Where Clause ({monitor.whereClause.length} element(s))
        </span>
        {monitor.whereClause.map((element, index) => (
          <div key={`${index}-${element.operator}`} className="generalStyles__flex mb-2">
            <div style={{ width: "100%" }}>
              <ContentFilterElementEditor
                element={element}
                onChangeElement={(updated) => onChangeFilterElementAt(index, updated)}
              />
            </div>
            <Tooltip title="Remove filter element">
              <Button
                onClick={() => onRemoveFilterElement(index)}
                className="buttonStyle__29"
                shape="circle"
                icon={<FontAwesomeIcon icon={faTrash} />}
              />
            </Tooltip>
          </div>
        ))}
        <Button onClick={onAddFilterElement} className="buttonStyle__28">
          <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
          Add Filter Element
        </Button>
      </div>
    </div>
  );
}

/** Events/alarms: event monitors (subscriptions with a SELECT+WHERE filter) and event-type -> LN2 message mappings. */
export default function EventsEditor({
  eventMonitors,
  onChangeEventMonitors,
  eventMappings,
  onChangeEventMappings,
}: {
  eventMonitors: OpcUaEventMonitorForm[];
  onChangeEventMonitors: (rows: OpcUaEventMonitorForm[]) => void;
  eventMappings: OpcUaEventToMessageRow[];
  onChangeEventMappings: (rows: OpcUaEventToMessageRow[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draftMonitor, setDraftMonitor] = useState<OpcUaEventMonitorForm>({
    node: { ...BLANK_NUMERIC_NODE_ID },
    selectClauses: [],
    whereClause: [],
    subscriptionName: "",
    queueSize: 1000,
  });
  const [draftMapping, setDraftMapping] = useState<OpcUaEventToMessageRow>({
    eventTypeNode: { ...BLANK_NUMERIC_NODE_ID },
    msgId: "",
    active: true,
  });

  const canAddMonitor = isNodeComplete(draftMonitor.node);
  const onAddMonitor = () => {
    if (!canAddMonitor) return;
    onChangeEventMonitors([...eventMonitors, draftMonitor]);
    setDraftMonitor({
      node: { ...BLANK_NUMERIC_NODE_ID },
      selectClauses: [],
      whereClause: [],
      subscriptionName: "",
      queueSize: 1000,
    });
  };
  const onRemoveMonitor = (index: number) =>
    onChangeEventMonitors(eventMonitors.filter((_, i) => i !== index));

  const canAddMapping = isNodeComplete(draftMapping.eventTypeNode) && draftMapping.msgId !== "";
  const onAddMapping = () => {
    if (!canAddMapping) return;
    onChangeEventMappings([...eventMappings, draftMapping]);
    setDraftMapping({ eventTypeNode: { ...BLANK_NUMERIC_NODE_ID }, msgId: "", active: true });
  };
  const onRemoveMapping = (index: number) =>
    onChangeEventMappings(eventMappings.filter((_, i) => i !== index));

  return (
    <div className="container mt-3">
      <Collapse defaultActiveKey={["event-monitors"]}>
        <Panel
          header={t("general.opcUaEventMonitors", { total: eventMonitors.length })}
          key="event-monitors"
        >
          {eventMonitors.map((monitor, index) => (
            <div
              key={`${monitor.node.namespaceIndex}-${monitor.node.identifier}-${index}`}
              className="mb-3 generalStyles__flex"
              style={{ backgroundColor: "#161616", borderRadius: "5px", padding: "10px" }}
            >
              <div style={{ width: "100%" }}>
                <div className="generalStyles__textEllipsis">
                  {describeNode(monitor.node)} — sub={monitor.subscriptionName || "(default)"};{" "}
                  {monitor.selectClauses.length} select clause(s); {monitor.whereClause.length}{" "}
                  where element(s)
                </div>
              </div>
              <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemoveMonitor(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
          <EventMonitorForm monitor={draftMonitor} onChangeMonitor={setDraftMonitor} />
          <div className="row mt-3">
            <div className="col-12 col-md-3">
              <Button
                onClick={onAddMonitor}
                disabled={!canAddMonitor}
                className={`generalStyles__width100 ${canAddMonitor ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add Event Monitor
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
      <br />
      <Collapse>
        <Panel
          header={t("general.opcUaEventToMessageMappings", { total: eventMappings.length })}
          key="event-mappings"
        >
          {eventMappings.map((row, index) => (
            <div
              key={`${row.eventTypeNode.namespaceIndex}-${row.eventTypeNode.identifier}-${row.msgId}-${index}`}
              className="mb-3 generalStyles__flex"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div style={{ width: "100%" }}>
                <div className="generalStyles__textEllipsis">
                  {describeNode(row.eventTypeNode)} → {row.msgId} (
                  {row.active ? "active" : "inactive"})
                </div>
              </div>
              <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemoveMapping(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
          <NodeIdEditor
            node={draftMapping.eventTypeNode}
            onChangeNode={(eventTypeNode) => setDraftMapping({ ...draftMapping, eventTypeNode })}
            label="Event Type"
          />
          <div className="row mt-2">
            <div className="col-12 col-md-6">
              <span className="generalStyles__inputLabel">LN2 Message</span>
              <SelectLn2Message
                value={draftMapping.msgId}
                onChange={(key) => setDraftMapping({ ...draftMapping, msgId: key })}
              />
            </div>
            <div className="col-12 col-md-3">
              <div className="generalStyles__flex">
                <span className="generalStyles__inputLabel">Active</span>
                <Switch
                  checked={draftMapping.active}
                  onChange={(checked) => setDraftMapping({ ...draftMapping, active: checked })}
                  style={{ marginLeft: 8 }}
                />
              </div>
            </div>
            <div className="col-12 col-md-3">
              <Button
                onClick={onAddMapping}
                disabled={!canAddMapping}
                className={`generalStyles__width100 ${canAddMapping ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
